// Các dòng của phiếu lương — MỘT nguồn cho mọi màn in phiếu (Lương của tôi,
// Chi tiết lương, phiếu in, bảng quản lý).
//
// Máy lương (backend/pkg/engines/payroll) cộng sẵn nhiều khoản vào nhau trước
// khi trả bản ghi:
//   • Hoa hồng (`commission`) ĐÃ GỒM hoa hồng bán thêm (upsell.go), Phụ phí
//     (`surcharges` — các phòng thợ và lễ tân), phần Phòng Kinh doanh
//     (sales_team.go) và 10% trang phục của thợ Profile.
//   • Mốc thưởng (`milestoneBonus`) ĐÃ GỒM thưởng mốc bán thêm (upsell.go) và
//     cả gói thưởng động lực (motivation.go: chuỗi ngày, quán quân, thang mốc).
// Màn nào tự xếp dòng từ các ô thô là dễ in một khoản hai lần — phiếu cộng
// không ra Thực lĩnh, người nhận lương thắc mắc mà không ai giải thích được.
//
// Nên: dòng CHÍNH cộng đúng ra `totalPay`; khoản đã nằm trong một dòng chính
// chỉ hiện ở nhóm "trong đó" của dòng ấy, thụt vào, không cộng. Bài
// `__tests__/payslip-lines.golden.test.ts` đòi điều đó trên bản ghi do CHÍNH
// máy Go sinh ra cho từng vai.
import { baseSalaryLabel } from "./base-salary-label";
import type { PayrollRecord } from "./lib";
import { installmentLabel, otherDeductionsOf, trainingFeesOf, type TrainingFeeItem } from "./trainingFee";

export type SlipLine = {
  key: string;
  label: string;
  /** Có dấu: khoản trừ là số âm. */
  amount: number;
  note?: string;
  /** Dòng trừ góp (phí đào tạo / bồi thường) — màn vẽ kèm kỳ và số còn lại. */
  trainingFee?: TrainingFeeItem;
};

export type PayslipLines = {
  main: SlipLine[];
  /** Dòng "trong đó" theo khoá dòng chính — đã nằm trong số của dòng chính, không cộng thêm. */
  within: Record<string, SlipLine[]>;
  /** Tổng các dòng chính — luôn bằng `totalPay` (dòng "Khoản khác" bù phần lệch nếu có). */
  total: number;
};

export const OTHER_LINE_KEY = "other";

const n = (v: number | undefined | null) => v ?? 0;

function line(key: string, label: string, amount: number, note?: string): SlipLine {
  return note ? { key, label, amount, note } : { key, label, amount };
}

function commissionWithin(r: PayrollRecord): SlipLine[] {
  const out: SlipLine[] = [];
  if (n(r.upsellCommission)) out.push(line("upsellCommission", "Hoa hồng bán thêm", n(r.upsellCommission)));
  // Lễ tân: Phụ phí CHÍNH LÀ hoa hồng quầy (department_reception.go) — nhắc lại
  // dưới dòng Hoa hồng chỉ in cùng một số hai lần. Phiếu GỘP (mySlip,
  // personSlips) mang sẵn phần lễ tân trong `receptionSurcharges`, vì phòng
  // chính của phiếu gộp không nói Phụ phí đến từ phòng nào; bản ghi thô của một
  // phòng thì xét theo phòng của chính nó.
  // Phiếu gộp nhận ra bằng `roles` (luôn có trên mySlip), KHÔNG bằng sự có mặt
  // của `receptionSurcharges`: trường đó bỏ trống khi bằng 0.
  const merged = r as { roles?: string[]; receptionSurcharges?: number };
  const fromReception = Array.isArray(merged.roles)
    ? n(merged.receptionSurcharges)
    : r.department === "reception"
      ? n(r.surcharges)
      : 0;
  const ownSurcharges = n(r.surcharges) - fromReception;
  if (ownSurcharges) out.push(line("surcharges", "Phụ phí", ownSurcharges));
  if (n(r.costumeExtraCommission)) {
    out.push(line("costumeExtra", "Trích tiền trang phục khách trả thêm", n(r.costumeExtraCommission)));
  }
  return out;
}

function milestoneWithin(r: PayrollRecord): SlipLine[] {
  const upsell = n(r.upsellMilestoneBonus);
  const motivation = n(r.motivationBonus);
  const streak = n(r.streakBonus);
  const champion = n(r.championBonus);
  const tiers = motivation - streak - champion;
  const rest = n(r.milestoneBonus) - upsell - motivation;
  const out: SlipLine[] = [];
  if (rest) out.push(line("clientMilestone", "Mốc thưởng theo số khách", rest));
  if (upsell) out.push(line("upsellMilestone", "Thưởng mốc bán thêm", upsell));
  if (streak) {
    out.push(
      line("streak", "Thưởng chuỗi ngày bán", streak, r.streakDays ? `${r.streakDays} ngày liên tiếp có bán` : undefined),
    );
  }
  if (champion) out.push(line("champion", "Quán quân phòng trong tháng", champion));
  if (tiers) out.push(line("motivationTiers", "Thưởng mốc tháng", tiers));
  return out;
}

export function payslipLines(r: PayrollRecord): PayslipLines {
  const main: SlipLine[] = [];
  const within: Record<string, SlipLine[]> = {};

  const base = n(r.baseSalary);
  const noBase = base === 0 && r.department === "photo_profile" && !r.baseSalaryPaidElsewhere;
  main.push(line("baseSalary", baseSalaryLabel(r), base, noBase ? "vai này không có lương cứng" : undefined));

  const push = (key: string, label: string, amount: number, note?: string) => {
    if (amount) main.push(line(key, label, amount, note));
  };
  push("hourlyPay", "Lương theo giờ", n(r.hourlyPay));
  push("overtimePay", "Tiền tăng ca", n(r.overtimePay));
  // Trợ lý Studio không bán gì: ô Hoa hồng của họ là tiền job doanh nghiệp.
  const commissionLabel = r.department === "studio_assistant" ? "Tiền job doanh nghiệp" : "Hoa hồng";
  push("commission", commissionLabel, n(r.commission), r.salesTeam ? "chi tiết ở mục Phòng Kinh doanh" : undefined);
  push("milestoneBonus", "Mốc thưởng", n(r.milestoneBonus));
  push("shiftLeadAllowance", `Phụ cấp ca trưởng (${r.shiftLeadCount ?? 0} ca)`, n(r.shiftLeadAllowance));
  push("prevMonthAdjustment", "Điều chỉnh tháng trước", n(r.prevMonthAdjustment));
  push("insurance", "Phụ cấp bảo hiểm", n(r.insurance));
  push("unionFee", "Công đoàn", -n(r.unionFee));
  push("bonusAmount", "Thưởng giám đốc duyệt", n(r.bonusAmount));
  push("deductionAmount", "Khấu trừ giám đốc duyệt", -n(r.deductionAmount));
  push("customAdditions", "Cộng tự do", n(r.customAdditions));
  push("customDeductions", "Trừ tự do", -otherDeductionsOf(r));
  // Dòng trừ góp hiện cả khi kỳ này trừ 0đ (chạm trần 30%): lời giải thích
  // "dồn sang tháng sau" là lý do dòng ấy tồn tại.
  for (const item of trainingFeesOf(r)) {
    main.push({ key: `trainingFee:${item.receivableId}`, label: installmentLabel(item), amount: -n(item.amount), trainingFee: item });
  }

  const commission = commissionWithin(r);
  if (commission.length && n(r.commission)) within.commission = commission;
  const milestone = milestoneWithin(r);
  if (milestone.length && n(r.milestoneBonus)) within.milestoneBonus = milestone;

  let total = main.reduce((s, l) => s + l.amount, 0);
  const gap = n(r.totalPay) - total;
  if (gap !== 0) {
    // Phiếu chốt theo khuôn cũ có thể thiếu trường mới: KHÔNG giấu tiền — nói
    // rõ có một khoản máy đã tính mà phiếu không tách được.
    main.push(line(OTHER_LINE_KEY, "Khoản khác", gap, "khoản máy đã tính nhưng phiếu cũ không tách"));
    total += gap;
  }
  return { main, within, total };
}
